import * as React from 'react';

import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';
import type {FooterButtonProps} from 'shells/select-picker-generic/select-picker-generic';
import {SelectPickerGenericCreatable} from 'shells/select-picker-generic/select-picker-generic-creatable';
import type {
    ActionMeta,
    SelectSize,
} from 'shells/select-picker-generic/select-picker-generic-creatable';
import {SelectPickerGenericMulti} from 'shells/select-picker-generic/select-picker-generic-multi';

type Props = {
    selectedId: string | null | undefined;
    selectedIds?: any[] | null | undefined;
    onChange: (newValue: any, actionMeta: ActionMeta) => any;
    onClearValue?: () => void;
    placeholder: string;
    labelKey?: string;
    valueKey?: string;
    autoFocus?: boolean;
    autoBlur?: boolean;
    isCreateable?: boolean;
    isMulti?: boolean;
    createableEntityText?: string;
    isLoading?: boolean;
    noResultsText?: string;
    styles?: any;
    menuPortalTarget?: HTMLElement;
    menuPlacement?: 'auto' | 'bottom' | 'top';
    models: any[];
    refetch?: () => Promise<any>;
    shouldRefetch?: boolean;
    defaultsToFirstItem?: boolean;
    hasError?: boolean | null | undefined;
    controlIcon?: React.ComponentType<any>;
    inputId?: string;
    hideDividerWhenEmpty?: boolean;
    loadOptions?: (arg1: string) => Promise<any>;
    footerButton?: FooterButtonProps;
    isDisabled?: boolean;
    formatOptionLabel?: (option: any) => React.ReactElement;
    size?: SelectSize;
    hideDivider?: boolean;
    onCreateOption?: (option: any) => void;
    menuZIndex?: number;
};

export class GraphQLSelect extends React.PureComponent<Props> {
    static defaultProps = {
        autoFocus: true,
        labelKey: 'name',
        valueKey: 'id',
        size: 'normal',
    };

    componentDidMount() {
        if (this.shouldDefaultToFirstItem(this.props)) {
            this.props.onChange(this.props.models[0], {
                action: 'select-option',
                // @ts-expect-error
                option: this.props.models[0],
            });
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        // If this currently has no models (aka a loading state),
        // and then gets models, optionally select the first element
        if (!this.props.models.length && this.shouldDefaultToFirstItem(nextProps)) {
            nextProps.onChange(nextProps.models[0], {
                action: 'select-option',
                // @ts-expect-error
                option: this.props.models[0],
            });
        }

        if (!this.props.shouldRefetch && nextProps.shouldRefetch && nextProps.refetch) {
            nextProps.refetch();
        }
    }

    render() {
        const {
            selectedId,
            selectedIds,
            noResultsText,
            models,
            autoBlur,
            onClearValue,
            defaultsToFirstItem, // eslint-disable-line @typescript-eslint/no-unused-vars
            isCreateable,
            isMulti,
            createableEntityText,
            labelKey,
            valueKey,
            loadOptions,
            refetch, // eslint-disable-line @typescript-eslint/no-unused-vars
            shouldRefetch, // eslint-disable-line @typescript-eslint/no-unused-vars
            hasError,
            controlIcon,
            hideDividerWhenEmpty,
            isDisabled,
            size,
            hideDivider,
            onCreateOption,
            ...restProps
        } = this.props;

        const selectValue = loadOptions
            ? null
            : models.find((model: any) => {
                  // @ts-expect-error
                  return model[valueKey] === selectedId;
              });

        // This is an array of selected ids used when isMulti
        const selectValues =
            !loadOptions && selectedIds
                ? selectedIds
                      .map((id) => {
                          // @ts-expect-error
                          return models.find((element) => element[valueKey] === id);
                      })
                      .filter((entry) => entry)
                : [];

        if (isCreateable) {
            return (
                <SelectPickerGenericCreatable
                    options={models}
                    noOptionsMessage={noResultsText ? () => noResultsText : undefined}
                    value={isMulti ? selectValues : selectValue}
                    // @ts-expect-error
                    formatCreateLabel={(label) =>
                        `+ Create ${createableEntityText || ''} "${label}"`
                    }
                    isDisabled={(this.props.isLoading && !models) || isDisabled}
                    blurInputOnSelect={autoBlur}
                    onClear={onClearValue}
                    controlIcon={controlIcon}
                    isMulti={false}
                    size={size}
                    hideDivider={hideDivider}
                    onCreateOption={onCreateOption}
                    {...restProps}
                />
            );
        }

        if (isMulti) {
            return (
                <SelectPickerGenericMulti
                    options={models}
                    noOptionsMessage={noResultsText ? () => noResultsText : undefined}
                    value={selectValues}
                    isDisabled={(this.props.isLoading && !models) || isDisabled}
                    blurInputOnSelect={autoBlur}
                    onClear={onClearValue}
                    isMulti={true}
                    labelKey={labelKey}
                    valueKey={valueKey}
                    hasError={hasError}
                    {...restProps}
                />
            );
        }

        return (
            <SelectPickerGeneric
                options={models}
                noOptionsMessage={noResultsText ? () => noResultsText : undefined}
                value={selectValue || null}
                isDisabled={(this.props.isLoading && !models) || isDisabled}
                blurInputOnSelect={autoBlur}
                onClear={onClearValue}
                labelKey={labelKey}
                valueKey={valueKey}
                hasError={hasError}
                controlIcon={controlIcon}
                hideDividerWhenEmpty={hideDividerWhenEmpty}
                {...restProps}
            />
        );
    }

    shouldDefaultToFirstItem = (props: Props) => {
        return props.models.length && props.defaultsToFirstItem && !props.selectedId;
    };
}
